import { useMemo } from "react";

import { AMENITY_CONSTANTS } from "@constants/amenities";
import { UseAmenitySummaryProps } from "@models/components/amenity.model";
import { BookedAmenityType } from "@models/components/user.model";
import { SelectedAmenity } from "@models/services/booking";
import { PriceSummaryProps } from "@models/services/pricing";
import { applyLocalization } from "@services/common.service";
import { getPriceInfo } from "@utils/amenity.utils";

const isAmenityMatch = (sa: SelectedAmenity, item: BookedAmenityType) => {
  if (sa.bookingAmenityId) {
    return sa.bookingAmenityId === item.bookingAmenityId;
  }
  return sa.id === item.id && sa.isBooked;
};

export const useAmenitySummary = ({
  amenitiesList,
  bookingEditData,
  currentLang,
  isEditBooking,
  resourceId,
  resourcePrices,
  selectedAmenitie,
  slot,
}: UseAmenitySummaryProps) => {
  return useMemo(() => {
    const resourceAmenityPrices = resourcePrices?.amenities?.[resourceId];

    if (!slot?.slot) return [];

    const summaryItems: NonNullable<PriceSummaryProps["amenities"]> = [];
    const newSummaryItems: NonNullable<PriceSummaryProps["amenities"]> = [];

    const vatPercentage = Number(
      resourcePrices?.vat?.vatPercentage ||
        AMENITY_CONSTANTS.DEFAULT_VAT_PERCENTAGE_VALUE
    );

    const isVatInclusive =
      resourcePrices?.vat?.hasVat &&
      resourcePrices?.vat?.vatHandlingType === "INCLUSIVE";

    const getTitle = (id: string) => {
      const master = amenitiesList.find(a => a.id === id);
      if (master?.title) {
        return applyLocalization(master?.title);
      }

      const editItem = bookingEditData?.amenities?.find(a => a.id === id);
      if (editItem && editItem?.title) {
        const locales = editItem.title?.locales as Record<string, string>;
        return (
          locales?.[currentLang] ||
          (typeof editItem.title?.default === "string"
            ? editItem.title.default
            : "") ||
          ""
        );
      }
      return "";
    };

    const getUnpaidInfo = (id: string, targetCount: number) => {
      const unpaidEntries =
        bookingEditData?.amenities?.filter(
          a =>
            a.id === id &&
            !a.isPaid &&
            selectedAmenitie.some(
              sa => sa && isAmenityMatch(sa, a) && sa.count > 0
            )
        ) || [];

      let remainingCount = targetCount;
      let totalPrice = 0;
      let totalVat = 0;
      let unitPriceTotal;

      for (const entry of unpaidEntries) {
        if (remainingCount <= 0) break;
        const take = Math.min(remainingCount, entry.quantity);
        const ratio = entry.quantity > 0 ? take / entry.quantity : 0;
        const entryPrice = isVatInclusive
          ? (entry.totalPriceWithVat ?? entry.totalPrice)
          : (entry.totalPriceWithOutVat ?? entry.unitPrice * entry.quantity);

        totalPrice += entryPrice * ratio;
        totalVat += Number(entry.vat || 0) * ratio;
        remainingCount -= take;
        unitPriceTotal = entry.unitPrice * entry.quantity;
      }
      return {
        price: totalPrice,
        remainingCount,
        unitPriceTotal,
        vat: totalVat,
      };
    };

    const getBackendCount = (itemId: string, totalCount: number) => {
      if (isEditBooking && bookingEditData?.amenities) {
        const matchedIndexes = new Set<number>();
        const selectedNonEditableQty = selectedAmenitie
          .filter(sa => sa && sa.id === itemId && sa.isBooked)
          .reduce((acc, sa) => {
            const origIndex = bookingEditData.amenities?.findIndex((a, idx) => {
              if (matchedIndexes.has(idx)) return false;
              if (sa.bookingAmenityId) {
                return a.bookingAmenityId === sa.bookingAmenityId;
              }
              return a.id === sa.id;
            });
            if (origIndex !== undefined && origIndex !== -1) {
              matchedIndexes.add(origIndex);
              const orig = bookingEditData.amenities?.[origIndex];
              return orig?.isEditable === false ? acc + sa.count : acc;
            }
            return acc;
          }, 0);
        return Math.max(0, totalCount - selectedNonEditableQty);
      }
      return totalCount;
    };

    const totalCountMap = new Map<string, number>();
    selectedAmenitie.forEach(sa => {
      if (sa) {
        totalCountMap.set(sa.id, (totalCountMap.get(sa.id) || 0) + sa.count);
      }
    });

    const amenityMap = new Map(Object.entries(resourceAmenityPrices || {}));

    if (isEditBooking && bookingEditData?.amenities) {
      const paidItems = bookingEditData.amenities.filter(item => item.isPaid);

      paidItems.forEach((item, index) => {
        const saMatch = selectedAmenitie.find(
          sa => sa && isAmenityMatch(sa, item)
        );
        const currentCount = saMatch ? saMatch.count : 0;
        if (currentCount === 0) return;

        const originalPrice = isVatInclusive
          ? (item.totalPriceWithVat ?? item.totalPrice)
          : (item.totalPriceWithOutVat ?? item.unitPrice * item.quantity);
        const unitPrice = item.quantity > 0 ? originalPrice / item.quantity : 0;
        const unitVat =
          item.quantity > 0 ? Number(item.vat || 0) / item.quantity : 0;

        summaryItems.push({
          count: currentCount,
          id: `${item.id}_orig_${index}`,
          title: getTitle(item.id),
          totalPrice: unitPrice * currentCount,
          unitPriceTotal: unitPrice * currentCount,
          vatAmount: unitVat * currentCount,
          vatPercentage,
        });
      });

      const paidSumMap = new Map<string, number>();
      paidItems.forEach(item => {
        const saMatch = selectedAmenitie.find(
          sa => sa && isAmenityMatch(sa, item)
        );
        const count = saMatch ? saMatch.count : 0;
        paidSumMap.set(item.id, (paidSumMap.get(item.id) || 0) + count);
      });

      const uniquePaidIds = new Set(paidItems.map(i => i.id));
      uniquePaidIds.forEach(itemId => {
        const totalCount = totalCountMap.get(itemId) || 0;
        if (totalCount === 0) {
          amenityMap.delete(itemId);
          return;
        }

        const paidSum = paidSumMap.get(itemId) || 0;
        const additionalCount = totalCount - paidSum;
        const base = amenityMap.get(itemId) as
          | {
              prices: {
                totalPrice: number;
                totalPriceWithTax?: number;
                vatAmount: number;
              };
            }
          | undefined;

        if (additionalCount > 0) {
          const { price: unpaidPrice, remainingCount } = getUnpaidInfo(
            itemId,
            additionalCount
          );
          const backendCount = getBackendCount(itemId, totalCount);
          const unitPrice =
            backendCount > 0
              ? (isVatInclusive
                  ? (base?.prices?.totalPriceWithTax ??
                    base?.prices?.totalPrice ??
                    0)
                  : (base?.prices?.totalPrice ?? 0)) / backendCount
              : 0;
          const unitVat =
            backendCount > 0
              ? (base?.prices?.vatAmount || 0) / backendCount
              : 0;
          newSummaryItems.push({
            count: additionalCount,
            id: `${itemId}_new`,
            title: getTitle(itemId),
            totalPrice: unitPrice * remainingCount + unpaidPrice,
            unitPriceTotal: unitPrice * additionalCount,
            vatAmount: unitVat * additionalCount,
            vatPercentage,
          });
        }

        amenityMap.delete(itemId);
      });
    }
    const processedIds = new Set<string>();
    if (isEditBooking && bookingEditData?.amenities) {
      bookingEditData.amenities
        .filter(item => item.isPaid)
        .forEach(item => processedIds.add(item.id));
    }

    amenityMap.forEach((base, key) => {
      const totalCount = totalCountMap.get(key) || 0;
      if (totalCount > 0) {
        const { price: unpaidPrice, remainingCount } = getUnpaidInfo(
          key,
          totalCount
        );
        const backendCount = getBackendCount(key, totalCount);
        const unitPrice =
          backendCount > 0
            ? (isVatInclusive
                ? (base.prices.totalPriceWithTax ?? base.prices.totalPrice)
                : base.prices.totalPrice) / backendCount
            : 0;
        const unitVat =
          backendCount > 0 ? base.prices.vatAmount / backendCount : 0;
        newSummaryItems.push({
          count: totalCount,
          id: key,
          title: getTitle(key),
          totalPrice: unitPrice * remainingCount + unpaidPrice,
          unitPriceTotal: unitPrice * totalCount,
          vatAmount: unitVat * totalCount,
          vatPercentage,
        });
        processedIds.add(key);
      }
    });

    if (isEditBooking && bookingEditData?.amenities) {
      const unpaidOnlyItems = bookingEditData.amenities.filter(
        item => !item.isPaid && !processedIds.has(item.id)
      );
      const unpaidOnlyMap = new Map<
        string,
        { totalQuantity: number; totalPrice: number; totalVat: number }
      >();
      unpaidOnlyItems.forEach(item => {
        const existing = unpaidOnlyMap.get(item.id);
        const priceVal = isVatInclusive
          ? (item.totalPriceWithVat ?? item.totalPrice)
          : (item.totalPriceWithOutVat ?? item.unitPrice * item.quantity);
        if (existing) {
          existing.totalQuantity += item.quantity;
          existing.totalPrice += priceVal;
          existing.totalVat += Number(item.vat || 0);
        } else {
          unpaidOnlyMap.set(item.id, {
            totalPrice: priceVal,
            totalQuantity: item.quantity,
            totalVat: Number(item.vat || 0),
          });
        }
      });
      unpaidOnlyMap.forEach((_, itemId) => {
        const totalCount = totalCountMap.get(itemId) || 0;
        if (totalCount > 0) {
          const {
            price: unpaidPrice,
            remainingCount,
            vat: unpaidVat,
          } = getUnpaidInfo(itemId, totalCount);

          let unitPrice = 0;
          let unitVat = 0;

          // Try to get from master list first
          const base = amenityMap.get(itemId) as
            | {
                prices: {
                  totalPrice: number;
                  totalPriceWithTax?: number;
                  vatAmount: number;
                };
              }
            | undefined;

          if (base) {
            const backendCount = getBackendCount(itemId, totalCount);
            unitPrice =
              backendCount > 0
                ? (isVatInclusive
                    ? (base.prices.totalPriceWithTax ?? base.prices.totalPrice)
                    : base.prices.totalPrice) / backendCount
                : 0;
            unitVat =
              backendCount > 0 ? base.prices.vatAmount / backendCount : 0;
          } else {
            // Fallback to the unpaid booking entry's unit price
            const unpaidEntries =
              bookingEditData?.amenities?.filter(
                a => a.id === itemId && !a.isPaid
              ) || [];
            if (unpaidEntries.length > 0) {
              const entry = unpaidEntries[0];
              const entryPrice = isVatInclusive
                ? (entry.totalPriceWithVat ?? entry.totalPrice)
                : (entry.totalPriceWithOutVat ??
                  entry.unitPrice * entry.quantity);
              unitPrice =
                entry.quantity > 0
                  ? entryPrice / entry.quantity
                  : entry.unitPrice;
              unitVat =
                entry.quantity > 0
                  ? Number(entry.vat || 0) / entry.quantity
                  : 0;
            }
          }

          const finalPrice = unpaidPrice + unitPrice * remainingCount;
          const finalVat = unpaidVat + unitVat * remainingCount;

          newSummaryItems.push({
            count: totalCount,
            id: `${itemId}_edit_only`,
            title: getTitle(itemId),
            totalPrice: finalPrice,
            unitPriceTotal: finalPrice,
            vatAmount: finalVat,
            vatPercentage,
          });
          processedIds.add(itemId);
        }
      });
    }

    // Process any selected amenities that are not in processedIds
    totalCountMap.forEach((totalCount, key) => {
      if (totalCount > 0 && !processedIds.has(key)) {
        const amenity = amenitiesList.find(a => a.id === key);
        const resourceData = amenity?.resources?.[resourceId];
        const priceInfo = resourceData
          ? getPriceInfo(resourceData.prices, slot.slot)
          : null;

        const isLocalVatInclusive =
          resourceData?.vat?.vatHandlingType === "INCLUSIVE";
        const localVatPercentage = Number(
          resourceData?.vat?.vatPercentage || 0
        );

        const unitPrice = priceInfo
          ? isLocalVatInclusive
            ? (priceInfo.totalPriceWithTax ?? priceInfo.totalPrice)
            : priceInfo.totalPrice
          : 0;

        const unitVat = priceInfo?.vatAmount ?? 0;

        newSummaryItems.push({
          count: totalCount,
          id: key,
          title: getTitle(key),
          totalPrice: unitPrice * totalCount,
          unitPriceTotal: unitPrice * totalCount,
          vatAmount: unitVat * totalCount,
          vatPercentage: localVatPercentage,
        });
        processedIds.add(key);
      }
    });

    return [...summaryItems, ...newSummaryItems];
  }, [
    resourcePrices,
    resourceId,
    slot?.slot,
    selectedAmenitie,
    amenitiesList,
    currentLang,
    isEditBooking,
    bookingEditData,
  ]);
};
